package com.vizio.pay.api

import com.vizio.redwolf.auth.api.PinEnvironment
import com.vizio.redwolf.auth.api.choosePinEnvironment
import com.vizio.redwolf.dms.homesetup.HomeSetupEnvironment
import com.vizio.redwolf.dms.homesetup.LinkedDevicesFilter
import com.vizio.redwolf.dms.homesetup.chooseHomeSetupEnvironment
import com.vizio.redwolf.utils.ConsentV2Environment
import com.vizio.redwolf.utils.ConsentV2EnvironmentConfig
import com.vizio.redwolf.utils.ConsentV2FusedEnvironment
import com.vizio.redwolf.utils.ConsentV2FusedEnvironmentConfig
import com.vizio.redwolf.utils.ConsentV2ProxyEnvironment
import com.vizio.redwolf.utils.ConsentV2ProxyEnvironmentConfig
import com.vizio.redwolf.utils.ECommerceEnvironmentConfig
import com.vizio.redwolf.utils.EnvironmentGroup
import com.vizio.redwolf.utils.HomeSetupEnvironmentConfig
import com.vizio.redwolf.utils.IdentityProxyEnvironment
import com.vizio.redwolf.utils.IdentityProxyEnvironmentConfig
import com.vizio.redwolf.utils.M2MEnvironment
import com.vizio.redwolf.utils.M2MEnvironmentConfig
import com.vizio.redwolf.utils.OffersEnvironment
import com.vizio.redwolf.utils.OffersEnvironmentConfig
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.PurchasePinEnvironmentConfig
import com.vizio.redwolf.utils.chooseConsentV2Environment
import com.vizio.redwolf.utils.chooseConsentV2FusedEnvironment
import com.vizio.redwolf.utils.chooseConsentV2ProxyEnvironment
import com.vizio.redwolf.utils.chooseIdentityProxyEnvironment
import com.vizio.redwolf.utils.chooseM2MEnvironment
import com.vizio.redwolf.utils.chooseOffersEnvironment
import com.vizio.redwolf.utils.choosePayEnvironment
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.promise
import kotlin.js.Promise

@JsExport
@JsName("JsEnvironmentGroup")
enum class JsEnvironmentGroup(val env: EnvironmentGroup) {
    PROD(EnvironmentGroup.PROD),
    PREPROD(EnvironmentGroup.PREPROD),
    STAGE(EnvironmentGroup.STAGE),
    DEV(EnvironmentGroup.DEV),
    TEST(EnvironmentGroup.TEST);

    @JsName("toSDKPayEnvironment")
    fun toSDKPayEnvironment(apiKey: String): PayEnvironment {
        // Message Token not supported in web, passing `null`
        return choosePayEnvironment(ECommerceEnvironmentConfig(this.env, apiKey, null))
    }

    @JsName("toSDKPinEnvironment")
    fun toSDKPinEnvironment(): PinEnvironment {
        return choosePinEnvironment(PurchasePinEnvironmentConfig(this.env))
    }

    @JsName("toSDKConsentV2Environment")
    fun toSDKConsentV2Environment(apiKey: String): ConsentV2Environment {
        return chooseConsentV2Environment(ConsentV2EnvironmentConfig(this.env, apiKey))
    }

    @JsName("toSDKConsentV2ProxyEnvironment")
    fun toSDKConsentV2ProxyEnvironment(): ConsentV2ProxyEnvironment {
        return chooseConsentV2ProxyEnvironment(ConsentV2ProxyEnvironmentConfig(this.env))
    }

    @JsName("toSDKConsentV2FusedEnvironment")
    fun toSDKConsentV2FusedEnvironment(apiKey: String): ConsentV2FusedEnvironment {
        return chooseConsentV2FusedEnvironment(ConsentV2FusedEnvironmentConfig(this.env, apiKey))
    }

    @JsName("toSDKHomeSetupEnvironment")
    fun toSDKHomeSetupEnvironment(dmsJwt: String, eCommApiKey: String, dmsV2ApiKey: String): HomeSetupEnvironment {
        val homeSetupEnvironmentConfig = HomeSetupEnvironmentConfig(
            environmentGroup = env,
            dmsJwt = dmsJwt,
            eCommApiKey = eCommApiKey,
            dmsApiKey = "",
            dmsV2ApiKey = dmsV2ApiKey
        )
        return chooseHomeSetupEnvironment(homeSetupEnvironmentConfig)
    }

    @JsName("toSDKIdentityProxyEnvironment")
    fun toSDKIdentityProxyEnvironment(): IdentityProxyEnvironment {
        val identityProxyEnvironmentConfig = IdentityProxyEnvironmentConfig(env)
        return chooseIdentityProxyEnvironment(identityProxyEnvironmentConfig)
    }

    @JsName("toSDKM2MEnvironment")
    fun toSDKM2MEnvironment(serviceId: String, serviceSecret: String, apiKey: String): M2MEnvironment {
        val m2mEnvironmentConfig = M2MEnvironmentConfig(
            environmentGroup = env,
            serviceId = serviceId,
            serviceSecret = serviceSecret,
            apiKey = apiKey
        )
        return chooseM2MEnvironment(m2mEnvironmentConfig)
    }

    @JsName("toSDKOffersEnvironment")
    fun toSDKOffersEnvironment(): OffersEnvironment {
        val offersEnvironmentConfig = OffersEnvironmentConfig(env)
        return chooseOffersEnvironment(offersEnvironmentConfig)
    }
}

@JsExport
@JsName("JsLinkedDevicesFilter")
enum class JsLinkedDevicesFilter {
    All,
    Pay,
    Alexa,
    Google;

    fun toSDKFilter(): LinkedDevicesFilter {
        return when(this){
            All -> LinkedDevicesFilter.All
            Pay -> LinkedDevicesFilter.Pay
            Alexa -> LinkedDevicesFilter.Alexa
            Google -> LinkedDevicesFilter.Google
        }
    }
}

data class JsExceptionInfo<T>(val caller: T)

fun <T: Any, R: Any, U: Any> handleJsPromiseRequest(jsExceptionInfo: JsExceptionInfo<U>? = null, exceptionHandler: (ex: Exception, data: JsExceptionInfo<U>?) -> R, apiRequest: suspend () -> T): Promise<dynamic> {
    return MainScope().promise {
        return@promise try {
            apiRequest()
        }
        catch (ex: Exception) {
            exceptionHandler(ex, jsExceptionInfo)
        }
    }
}

@JsExport
@JsName("JsArrayWrapper")
data class JsArrayWrapper<T : Array<Any?>>(val items: T)

const val jsRequestExceptionError = "An error has occurred due to a issue with the request. Please check that the request follows the correct format!"